import React, { useEffect, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { api_url } from "../../constant/constant";
import { useTranslation } from "react-i18next";
import { useNavigate, NavLink } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { showToast } from "../../Components/ShowToast/ToastNotification";

const ComplianceCampaign = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [links, setLinks] = useState([]);
  const [searchVal, setSearchVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    whiteSpace: "nowrap",
  };

  const cellTextStyle = {
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";

    return new Date(dateValue).toLocaleDateString();
  };

  const formatBoolean = (value) => {
    return value ? t("Yes") : t("No");
  };

  const fetchComplianceCampaign = async (url = "") => {
    setLoading(true);

    try {
      const token = localStorage.getItem("userToken");

      const response = await fetch(url || `${api_url}/compliance-campaigns`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const campaignData = result?.data?.data || [];

      setCampaigns(campaignData);
      setFilteredCampaigns(campaignData);
      setLinks(result?.data?.links || []);
    } catch (error) {
      console.error("Error fetching compliance campaigns:", error);
      showToast(t("errorfetchingdata"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;

    setLoading(true);

    try {
      const token = localStorage.getItem("userToken");

      const response = await fetch(`${api_url}/compliance-campaigns/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        showToast(t("ComplianceCampaigndeletedsuccessfully"), "success");
        setShowModal(false);
        setSelectedCampaignId(null);
        fetchComplianceCampaign();
      } else {
        showToast(t("failedtodeleteComplianceCampaign"), "error");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      showToast(t("somethingwentwrong"), "error");
    } finally {
      setShowModal(false);
      setLoading(false);
    }
  };

  const handleSendComplianceEmails = async (id) => {
    if (!id) return;

    try {
      const token = localStorage.getItem("userToken");

      const response = await fetch(
        `${api_url}/compliance-campaigns/${id}/send-emails`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (response.ok && result.status === "success") {
        showToast(result.message || "Emails initiated successfully.", "success");
        fetchComplianceCampaign();
      } else {
        showToast(result.message || "Failed to initiate emails.", "error");
      }
    } catch (error) {
      console.error("Email sending failed:", error);
      showToast("An error occurred while sending compliance emails.", "error");
    }
  };

  const handleCampaignRowClick = (id) => {
    navigate(`/ComplianceCampaignDetail/${id}/ComplianceCampaignDetail`);
  };

  useEffect(() => {
    fetchComplianceCampaign();
  }, []);

  useEffect(() => {
    const value = searchVal.trim().toLowerCase();

    if (!value) {
      setFilteredCampaigns(campaigns);
      return;
    }

    const filteredData = campaigns.filter((item) => {
      return (
        (item.title_en || "").toLowerCase().includes(value) ||
        (item.title_ar || "").toLowerCase().includes(value) ||
        (item.description_en || "").toLowerCase().includes(value) ||
        (item.description_ar || "").toLowerCase().includes(value) ||
        (item.type || "").toLowerCase().includes(value) ||
        (item.start_date || "").toLowerCase().includes(value) ||
        (item.end_date || "").toLowerCase().includes(value) ||
        (item.start_time || "").toLowerCase().includes(value) ||
        String(item.is_active).toLowerCase().includes(value) ||
        String(item.is_completed).toLowerCase().includes(value)
      );
    });

    setFilteredCampaigns(filteredData);
  }, [searchVal, campaigns]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "title_en",
        header: () => (
          <div style={headerStyle}>
            <i className="bi bi-clipboard-data"></i>
            {t("Title_En")}
          </div>
        ),
        cell: ({ getValue }) => <span>{getValue() || "-"}</span>,
        colName: t("Title_En"),
      },
      {
        accessorKey: "title_ar",
        header: () => (
          <div style={headerStyle}>
            <i className="bi bi-clipboard-data"></i>
            {t("Title_Ar")}
          </div>
        ),
        cell: ({ getValue }) => <span>{getValue() || "-"}</span>,
        colName: t("Title_Ar"),
      },
      {
        accessorKey: "description_en",
        header: () => (
          <div style={headerStyle}>
            <i className="bi bi-card-text"></i>
            {t("Description_En")}
          </div>
        ),
        cell: ({ getValue }) => <span>{getValue() || "-"}</span>,
        colName: t("Description_En"),
      },
      {
        accessorKey: "description_ar",
        header: () => (
          <div style={headerStyle}>
            <i className="bi bi-card-text"></i>
            {t("Description_Ar")}
          </div>
        ),
        cell: ({ getValue }) => <span>{getValue() || "-"}</span>,
        colName: t("Description_Ar"),
      },
      {
        accessorKey: "type",
        header: () => (
          <div style={headerStyle}>
            <i className="bi bi-list-check"></i>
            {t("Type")}
          </div>
        ),
        cell: ({ getValue }) => <span>{getValue() || "-"}</span>,
        colName: t("Type"),
      },
      {
        accessorKey: "start_date",
        header: () => (
          <div style={headerStyle}>
            <i className="bi bi-calendar-event"></i>
            {t("StartDate")}
          </div>
        ),
        cell: ({ getValue }) => <span>{formatDate(getValue())}</span>,
        colName: t("StartDate"),
      },
      {
        accessorKey: "end_date",
        header: () => (
          <div style={headerStyle}>
            <i className="bi bi-calendar-check"></i>
            {t("EndDate")}
          </div>
        ),
        cell: ({ getValue }) => <span>{formatDate(getValue())}</span>,
        colName: t("EndDate"),
      },
      {
        accessorKey: "start_time",
        header: () => (
          <div style={headerStyle}>
            <i className="bi bi-clock"></i>
            {t("StartTime")}
          </div>
        ),
        cell: ({ getValue }) => <span>{getValue() || "-"}</span>,
        colName: t("StartTime"),
      },
      {
        accessorKey: "is_active",
        header: () => (
          <div style={headerStyle}>
            <i className="bi bi-toggle-on"></i>
            {t("IsActive")}
          </div>
        ),
        cell: ({ getValue }) => <span>{formatBoolean(getValue())}</span>,
        colName: t("IsActive"),
      },
      {
        accessorKey: "is_completed",
        header: () => (
          <div style={headerStyle}>
            <i className="bi bi-check-circle"></i>
            {t("IsCompleted")}
          </div>
        ),
        cell: ({ getValue }) => <span>{formatBoolean(getValue())}</span>,
        colName: t("IsCompleted"),
      },
      {
        accessorKey: "id",
        header: () => (
          <div style={headerStyle}>
            <img src="/images/fi_alert-octagon.png" alt="Action" />
            {t("Action")}
          </div>
        ),
        cell: ({ getValue }) => {
          const id = getValue();

          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                minWidth: "210px",
                whiteSpace: "nowrap",
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedCampaignId(id);
                  setShowModal(true);
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#dc3545",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  cursor: "pointer",
                  padding: "4px 6px",
                  whiteSpace: "nowrap",
                  minWidth: "78px",
                }}
              >
                <img src="/images/fi_trash-2.png" alt="Delete" />
                <span style={{ whiteSpace: "nowrap" }}>{t("Delete")}</span>
              </button>

              <Button
                type="button"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSendComplianceEmails(id);
                }}
                style={{
                  backgroundColor: "#214f86",
                  borderColor: "#214f86",
                  color: "#fff",
                  fontWeight: "600",
                  whiteSpace: "nowrap",
                  minWidth: "100px",
                  padding: "6px 12px",
                }}
              >
                {t("SendEmail")}
              </Button>
            </div>
          );
        },
        colName: t("Action"),
      },
    ],
    [t]
  );

  const table = useReactTable({
    data: filteredCampaigns,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="p-4 mb-4">
      <div className="pageTitle mb-4">{t("AllComplianceCampaign")}</div>

      <div className="primaryBlueBox p-5 mb-4">
        <div className="row">
          <div className="col-md-4">
            <label className="form-label">
              {t("SearchComplianceCampaign")}
            </label>

            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder={t("Searchbyname")}
                aria-label="Search Compliance Campaign"
                value={searchVal}
                onChange={(e) => setSearchVal(e.target.value)}
              />

              <button
                className="btn btn-outline-secondary btnSearchIcon"
                type="button"
              >
                <i className="bi bi-search"></i>
              </button>
            </div>
          </div>

          <div className="col-md-1">
            <div className="vrLine"></div>
          </div>

          <div className="col-md-7">
            <NavLink
              className="btnWhiteGradient mt-4"
              to="/Add-ComplianceCampaign"
            >
              <i className="bi bi-envelope me-2 mt-3 h6"></i>
              {t("AddComplianceCampaign")}
            </NavLink>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center mt-5">
          <div
            className="spinner-border loader-api"
            role="status"
            style={{
              borderTopColor: "#6897CB",
              borderBottomColor: "#6897CB",
              borderLeftColor: "#6897CB",
              marginTop: "50px",
            }}
          ></div>
        </div>
      ) : (
        <div className="bg-light px-3 py-3 lessonsTableWrapper resTable overflow-auto">
          {window.innerWidth < 768 ? (
            table.getRowModel().rows.map((row) => (
              <div
                key={row.id}
                className="stacked-row"
                onClick={() => handleCampaignRowClick(row.original.id)}
              >
                {row.getVisibleCells().map((cell) => (
                  <div key={cell.id} className="stacked-cell">
                    <strong className="me-1">
                      {cell.column.columnDef.colName}:
                    </strong>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ))}
              </div>
            ))
          ) : (
            <table
              className="table table-bordered table-hover"
              style={{
                minWidth: "1450px",
                tableLayout: "auto",
              }}
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          whiteSpace: "nowrap",
                          verticalAlign: "middle",
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="rowAnchor"
                          style={cellTextStyle}
                          onClick={
                            cell.column.id !== "id"
                              ? () => handleCampaignRowClick(row.original.id)
                              : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="text-center py-4"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {t("NoDataFound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="navigationWrapper">
        <ul>
          {links.map((link, index) => (
            <li
              key={index}
              className={`${link.active ? " active " : ""} ${
                link.url === null ? " disabled " : ""
              }`}
              onClick={
                link.url !== null
                  ? () => fetchComplianceCampaign(link.url)
                  : undefined
              }
            >
              <div dangerouslySetInnerHTML={{ __html: link.label }} />
            </li>
          ))}
        </ul>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("ConfirmDelete")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {t("AreyousureyouwanttodeletethisComplianceCampaign")}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            {t("Cancel")}
          </Button>

          <Button
            variant="danger"
            onClick={() => handleDelete(selectedCampaignId)}
            disabled={loading}
          >
            {loading ? t("Deleting...") : t("Delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ComplianceCampaign;